import React, { useState, useEffect, useContext } from 'react';
import useAsyncEffect from 'use-async-effect';
import { useHistory } from 'hooks/router';
import { Form } from 'react-bootstrap';
import Inputs from 'components/inputs/inputs';
import Flow from 'components/common/flow/flow';
import Panel from 'components/common/panel/panel';
import Heading from 'components/common/heading/heading';
import SoftwareUpdateService from 'services/softwareUpdateService';
import SoftwareUpdateHelper from 'components/navigation/softwareUpdate/softwareUpdateHelper';
import SiteSelectorFormControl from 'components/shared/siteSelector/siteSelectorFormControl';
import { Tabs, Tab } from 'components/common/tabs/tabs';
import AppContext from 'contexts/appContext';
import useForm from 'hooks/form';
import moment from 'moment';
import './softwareUpdateCreatePage.css';

const styles = {
  numberInput: {
    display: 'inline',
    width: '70px',
    textAlign: 'center'
  }
};

export default function SoftwareUpdateCreatePage(props) {
  const { appState, setAppArea, setAppBreadcrumb } = useContext(AppContext);

  const { setFormRef, submitForm, renderFormValidationMessage } = useForm({ protect: true });

  const [softwareUpdateName, setSoftwareUpdateName] = useState('');
  const [selectedType, setSelectedType] = useState();
  const [softwareVersionId, setSoftwareVersionId] = useState();
  const [distributeDateTime, setDistributeDateTime] = useState();
  const [softwareTypes, setSoftwareTypes] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [softwareTypeVersionsMap, setSoftwareTypeVersionsMap] = useState({});
  const [isAsapSelected, setAsapSelected] = useState(false);
  const [isOnHoldSelected, setOnHoldSelected] = useState(false);
  const [updateRateVolume, setUpdateRateVolume] = useState(2);
  const [updateRateFrequency, setUpdateRateFrequency] = useState(1);
  const [distributeOptionCode, setDistributeOptionCode] = useState(false);

  const history = useHistory();
  const autoPopulate = props.location.state ? props.location.state.from : null;

  useAsyncEffect(async () => {
    setSoftwareTypes(await SoftwareUpdateHelper.getSoftwareTypes());
    setSoftwareTypeVersionsMap(await SoftwareUpdateHelper.getSoftwareVersionsForUpdate());
  }, []);

  useEffect(() => {
    setAppArea(AppContext.Areas.SoftwareUpdate, 'Update Setup');
    setAppBreadcrumb([{ name: 'Home', href: '/' }, { name: 'Software Update', href: '/SoftwareUpdate' }, { name: 'Create Update', selected: true }]);

    if (autoPopulate && autoPopulate.siteId) {
      setSelectedSites([autoPopulate.siteId]);
      setSelectedType(autoPopulate.softwareTypeId);
      setSoftwareVersionId(autoPopulate.versionId);
    }
  }, [autoPopulate]);

  const handleSaveUpdateClicked = submitForm(async (cancelSubmit) => {
    const id = await SoftwareUpdateService.API.insertSoftwareUpdate({
      softwareUpdateName,
      softwareVersionId,
      distributeDateTime,
      siteIds: selectedSites,
      distributeAsap: isAsapSelected,
      distributeOnHold: isOnHoldSelected,
      updateRateVolume,
      updateRateFrequency,
      distributeOptionCode,
    });
    history.replace(`/SoftwareUpdate/${id}`);
  });

  const handleCancelClicked = () => {
    props.history.goBack();
  };

  const handleSoftwareTypeChange = (newValue) => {
    setSelectedType(parseInt(newValue));
    setDistributeOptionCode(null);
    if (softwareTypeVersionsMap[newValue] && softwareTypeVersionsMap[newValue].length > 0) {
      const firstVersion = softwareTypeVersionsMap[newValue][0];
      setSoftwareVersionId(firstVersion.id);
    }
  };

  const renderAdditionalOptions = () => {
    switch (selectedType) {
      case 3:
        return (
          <Form.Group>
            <Form.Label>QuickMenu Options</Form.Label>
            <Inputs.Checkbox
              label='Run QM database repair after update'
              checked={distributeOptionCode === 'repair'}
              onValueChange={(v) => setDistributeOptionCode(v ? 'repair' : null)}
              block
            />
          </Form.Group>
        );
      default:
        return null;
    }
  };

  return (
    <Flow>
      <Flow.Header>
        <Heading>
          <div className='flex-grow-1 d-flex justify-content-end'>
            <Inputs.Button variant='default' onClick={handleCancelClicked} iconClass='icons8-undo' label='Cancel' />
            <Inputs.Button variant='success' onClick={handleSaveUpdateClicked} iconClass='icons8-save' label='Save' />
          </div>
        </Heading>
      </Flow.Header>

      <Flow.Body>
        <div className='row justify-content-md-center mx-0 mt-4'>
          <div className='col-md-10 col-lg-8 col-xl-6'>
            <Form ref={setFormRef} noValidate>
              <Panel>
                <Panel.Header>Create Update</Panel.Header>
                <Panel.Body>
                  <Form.Group>
                    <Form.Label required>Description</Form.Label>
                    <Inputs.Text
                      required
                      placeholder='Please enter a description of the update'
                      description='Description'
                      value={softwareUpdateName}
                      onValueChange={setSoftwareUpdateName}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label required>Software</Form.Label>
                    <Inputs.Select
                      required
                      placeholder='Please select software'
                      description='Software'
                      value={selectedType}
                      onValueChange={handleSoftwareTypeChange}
                    >
                      {softwareTypes
                        .filter((item) => item.isUpdatable)
                        .map((item) => (
                          <Inputs.Option value={item.id} label={item.name} key={item.id} />
                        ))}
                    </Inputs.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label required>Version</Form.Label>
                    <Inputs.Select
                      required
                      placeholder='Please select version'
                      description='Version'
                      value={softwareVersionId}
                      onValueChange={(v) => setSoftwareVersionId(parseInt(v))}
                    >
                      {selectedType && (softwareTypeVersionsMap[selectedType] || []).map((item) => (
                        <Inputs.Option value={item.id} key={item.id} >
                          {item.name} {item.buildDate ? '(' + moment(item.buildDate).format('L') + ')' : ''}
                        </Inputs.Option>
                      ))}
                    </Inputs.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label required>Sites</Form.Label>
                    <SiteSelectorFormControl
                      required
                      sitesSelected={selectedSites}
                      onChange={(ids) => setSelectedSites(ids)}
                      areaId={appState.area.id}
                      selectableFn={SoftwareUpdateHelper.siteFilterFn}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Schedule</Form.Label>
                    <Tabs
                      defaultSelection='custom'
                      onSelect={(key) => {
                        setAsapSelected(key === 'asap');
                        setOnHoldSelected(key === 'onhold');
                      }}
                    >
                      <Tab value='custom' title='Scheduled'>
                        <Form.Group>
                          <Form.Label required>Update Date &amp; Time</Form.Label>
                          <Inputs.DateTime
                            required
                            placeholder='Please select update date and time'
                            description='Update Date & Time'
                            help='Site Local Time'
                            value={distributeDateTime}
                            onChange={(date) => setDistributeDateTime(date)}
                          />
                        </Form.Group>
                      </Tab>
                      <Tab value='asap' title='ASAP'>
                        <div>This update will begin as soon as possible.</div>
                      </Tab>
                      <Tab value='onhold' title='On Hold'>
                        <div>This update will be on hold. It will not execute until manually removed from the hold.</div>
                      </Tab>
                    </Tabs>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label required>Throttling</Form.Label>
                    <div>
                      <span className='mr-2'>Run an update at</span>
                      <Inputs.Integer
                        required
                        min='1'
                        max='999'
                        value={updateRateVolume}
                        onValueChange={(val) => setUpdateRateVolume(val)}
                        style={styles.numberInput}
                      />
                      <span className='mx-2'>sites every</span>
                      <Inputs.Integer
                        required
                        min='1'
                        max='120'
                        value={updateRateFrequency}
                        onValueChange={(val) => setUpdateRateFrequency(val)}
                        style={styles.numberInput}
                      />
                      <span className='ml-2'>minutes</span>
                    </div>
                  </Form.Group>
                  {renderAdditionalOptions()}
                </Panel.Body>
                <Panel.Footer>
                  {renderFormValidationMessage()}
                  <Inputs.Button variant='default' onClick={handleCancelClicked} iconClass='icons8-undo' label='Cancel' />
                  <Inputs.Button variant='success' onClick={handleSaveUpdateClicked} iconClass='icons8-save' label='Save' />
                </Panel.Footer>
              </Panel>
            </Form>
          </div>
        </div>
      </Flow.Body>
    </Flow>
  );
}
